import { Component, PLATFORM_ID, Inject, Input, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';

@Component({
  selector: 'app-vertical-bar-simple-chart',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './vertical-bar-simple-chart.html',
  styleUrls: ['./vertical-bar-simple-chart.scss']
})
export class VerticalBarSimpleChart implements OnChanges {
  @Input() data: number[] = [65, 59, 80, 81, 56, 55];
  @Input() labels: string[] = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
  @Input() barColor: string | string[] = '#001E50'; // Agora aceita string ou array de strings
  @Input() showValues: boolean = true;
  @Input() title: string = '';
  @Input() decimalPlaces: number = 0; // ALTERADO: padrão 0 casa decimal

  chartOption: EChartsOption;
  isBrowser: boolean;
  showChart = true;
  private currentIsSmallScreen = false;
  private chartInstance: any;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.currentIsSmallScreen = this.getIsSmallScreen();
    this.chartOption = this.getChartOptions(this.currentIsSmallScreen);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isBrowser && (changes['data'] || changes['labels'] || changes['showValues'] || changes['title'] || changes['decimalPlaces'])) {
      this.chartOption = this.getChartOptions(this.currentIsSmallScreen);
    }
  }

  onChartInit(ec: any) {
    this.chartInstance = ec;
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      const newIsSmallScreen = this.getIsSmallScreen();

      if (newIsSmallScreen !== this.currentIsSmallScreen) {
        this.currentIsSmallScreen = newIsSmallScreen;

        this.showChart = false;
        setTimeout(() => {
          this.chartOption = this.getChartOptions(this.currentIsSmallScreen);
          this.showChart = true;

          setTimeout(() => {
            if (this.chartInstance) {
              this.chartInstance.resize();
            }
          }, 100);
        }, 50);
      } else {
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      }
    }
  }

  private getIsSmallScreen(): boolean {
    return this.isBrowser && window.innerWidth <= 1600;
  }

private getChartOptions(isSmallScreen: boolean): EChartsOption {
  // Determina as cores para cada barra
  const colors = Array.isArray(this.barColor)
    ? this.barColor
    : this.data.map(() => this.barColor as string);

  // Calcula o valor máximo dos dados
  const maxDataValue = this.data.length > 0 ? Math.max(...this.data) : 100;

  // Define o espaço superior do grid baseado no valor máximo
  let gridTop = this.title ? '12%' : '5%';

  if (maxDataValue > 1000000) {
    gridTop = this.title ? '20%' : '15%';
  } else if (maxDataValue > 100000) {
    gridTop = this.title ? '18%' : '13%';
  } else if (maxDataValue > 10000) {
    gridTop = this.title ? '15%' : '10%';
  } else if (maxDataValue > 1000) {
    gridTop = this.title ? '14%' : '8%';
  } else if (maxDataValue > 100) {
    gridTop = this.title ? '13%' : '7%';
  }

  // Ajusta para telas pequenas
  if (isSmallScreen) {
    gridTop = this.title ? '15%' : '8%';
  }

  // Define o espaço inferior - AUMENTADO para reduzir espaço em branco embaixo
  const gridBottom = '15%'; // Aumentado de 8% para 15%

  // Define o máximo do eixo Y com margem para os labels
  const yAxisMax = maxDataValue > 0 ? maxDataValue * 1.2 : 100;

  // Função para formatar valores com casas decimais
  const formatValue = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(this.decimalPlaces)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(this.decimalPlaces)}K`;
    }
    return value.toFixed(this.decimalPlaces);
  };

  // Função para formatar tooltip com casas decimais
  const formatTooltipValue = (value: number): string => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: this.decimalPlaces,
      maximumFractionDigits: this.decimalPlaces
    });
  };

  return {
    animation: false,
    title: this.title ? {
      text: this.title,
      left: 'left',
      top: 0,
      textStyle: {
        color: '#001E50',
        fontWeight: 'bold',
        fontSize: isSmallScreen ? 14 : 16
      }
    } : undefined,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const value = params[0].value;
        const label = params[0].name;
        return `${label}: ${formatTooltipValue(value)}`;
      }
    },
    grid: {
      left: '3%',
      right: '3%',
      top: gridTop, // Usa o valor dinâmico calculado (ORIGINAL)
      bottom: 0, // AUMENTADO para mover eixo X para cima
      containLabel: false
    },
    xAxis: {
      type: 'category',
      data: this.labels,
      axisLabel: {
        color: '#6A767D',
        fontSize: isSmallScreen ? 10 : 9,
        interval: 0,
        formatter: (value: string) => {
          // Quebra o texto em duas linhas se for muito longo
          if (value.length > 8) {
            return value.split(' ').join('\n');
          }
          return value;
        },
        lineHeight: 14,
        margin: 12,
        rotate: 45
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      max: yAxisMax, // Define um máximo para o eixo Y
      axisLabel: {
        show: false
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        show: false
      }
    },
    series: [
      {
        type: 'bar',
        data: this.data.map((value, index) => ({
          value: value,
          itemStyle: {
            color: colors[index]
          }
        })),
        barWidth: isSmallScreen ? '27px' : '37px', // Largura original das barras
        barGap: '30%', // Espaçamento original entre barras
        label: this.showValues ? {
          show: true,
          position: 'top',
          color: '#6A767D',
          fontWeight: 'normal',
          fontSize: isSmallScreen ? 11 : 13,
          formatter: (params: any) => {
            return formatValue(params.value);
          },
          offset: [0, 3] // Pequeno offset para cima
        } : undefined
      }
    ]
  };
}
}
